import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { LOGOUT_REASON } from "common/constants";
import { buildLink } from "common/routing";
import {
  CurrentUserSettingsQuery,
  UpdatePasswordMutationVariables,
  useUpdatePasswordMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import {
  validatePasswordsMatch,
  validatePasswordWithTranslations,
} from "../../utils/passwords";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../Common/__styles__/Modal";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import { Text } from "../Inputs/react-hook-form";
import { PasswordNote } from "./__styles__/UpdatePasswordModal";

type UpdatePasswordModalHookProps = Partial<{
  user: CurrentUserSettingsQuery["currentUser"];
}>;

export const useUpdatePasswordModal = () => {
  const [props, setProps] = useState<UpdatePasswordModalHookProps>({});
  const [show, hide] = useModal(
    () => (
      <Modal onRequestClose={hide}>
        <UpdatePasswordModal onCancel={hide} onSave={hide} {...props} />
      </Modal>
    ),
    [props],
  );

  const showWithProps = (
    additionalProps: UpdatePasswordModalHookProps = {},
  ) => {
    setProps(additionalProps);
    show();
  };

  return [showWithProps, hide] as const;
};

type UpdatePasswordFormProps = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

export interface UpdatePasswordModalProps {
  onSave: () => void;
  onCancel: () => void;
  user?: CurrentUserSettingsQuery["currentUser"];
}

const UpdatePasswordModal = ({
  onCancel,
  onSave,
  user,
}: UpdatePasswordModalProps) => {
  const { t } = useTranslation();
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    getFieldState,
    formState: { errors, isDirty, isValid },
  } = useForm<UpdatePasswordFormProps>({ delayError: 200, mode: "onChange" });

  const [updatePassword, { loading }] = useUpdatePasswordMutation({
    onCompleted: () => {
      addSuccessToast(
        `Password successfully reset. Redirecting to login page.`,
      );
      onSave();

      setTimeout(() => {
        // Redirect to logout page to force a new session
        window.location.href = buildLink(
          "logout",
          {},
          { logoutReason: LOGOUT_REASON.PASSWORD_RESET },
        );
      }, 2000);
    },
    onError: error => {
      if (error.graphQLErrors[0]?.extensions?.code === "USER_ERROR") {
        const property =
          error.message.match(/password is incorrect/i) ?
            "currentPassword"
          : "confirmNewPassword";
        setError(property, { message: error.message }, { shouldFocus: true });
      } else {
        addErrorToast(
          `There was an error resetting your password. Please try again. If the problem persists, please email us at support@withforerunner.com`,
        );
      }
    },
  });

  const submitForm = async (userProvidedData: UpdatePasswordFormProps) => {
    const { currentPassword, newPassword } = userProvidedData;
    const payload: UpdatePasswordMutationVariables = {
      currentPassword,
      newPassword,
    };
    await updatePassword({ variables: payload });
  };

  if (!user) {
    return null;
  }

  const minimumPasswordLength =
    user.account.securityConfiguration.minimumPasswordLength;

  return (
    <Container>
      <HeaderSection>
        <h1>Reset password</h1>
      </HeaderSection>
      <FormSection onSubmit={handleSubmit(submitForm)}>
        <ContentSection>
          <InputRow>
            <Text
              label="Current password"
              placeholder="Current password"
              type="password"
              data-testid="current-password"
              error={errors.currentPassword?.message}
              {...register("currentPassword", {
                required: "Required field",
              })}
            />
          </InputRow>
          <InputRow>
            <Text
              label="New password"
              placeholder="New password"
              type="password"
              data-testid="new-password"
              error={errors.newPassword?.message}
              {...register("newPassword", {
                required: "Required field",
                deps: ["confirmNewPassword"],
                validate: {
                  passwordIsValid: (newPassword: string) =>
                    validatePasswordWithTranslations({
                      minimumPasswordLength,
                      password: newPassword,
                      t,
                    }),
                },
              })}
            />
          </InputRow>
          <InputRow>
            <Text
              label="Confirm new password"
              placeholder="Confirm new password"
              type="password"
              data-testid="confirm-new-password"
              error={errors.confirmNewPassword?.message}
              {...register("confirmNewPassword", {
                validate: {
                  passwordsMatch: (confirmNewPassword: string) => {
                    const userHasEnteredValue =
                      getFieldState("confirmNewPassword").isDirty;
                    return (
                      userHasEnteredValue &&
                      validatePasswordsMatch({
                        password: getValues("newPassword"),
                        confirmPassword: confirmNewPassword,
                        t,
                      })
                    );
                  },
                },
              })}
            />
          </InputRow>
          <PasswordNote>
            Note: please use at least {minimumPasswordLength} characters in your
            password and include at least one number, one upper case letter, and
            one special character.
          </PasswordNote>
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button
              onClick={onCancel}
              size="medium"
              styleVariant="secondary"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="medium"
              styleVariant="primary"
              type="submit"
              disabled={loading || !isDirty || !isValid}
            >
              Save
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default UpdatePasswordModal;
